import React, { Fragment, useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import { isEmpty, toString } from 'lodash'
import classNames from 'classnames'
import { IconArrowBackUp, IconTrash } from '@tabler/icons-react'

import { Loader } from '@/components/loaders/Loader'
import { Button } from '@/components/buttons/Button'
import { Card } from '@/components/cards/Card'
import { updateMediaPlayer, resetMediaPlayer } from '@/reducers/media/media.redux'
import { COACHING_CALL_DETAIL_PAGE, trackPageView } from '@/reducers/callSearch/callSearch.actions'
import { MediaPlayer } from '@/components/media/MediaPlayer'
import { AudioPlayerProvider } from '@/components/media/AudioPlayerContext'
import { VideoPlayerProvider } from '@/components/media/VideoPlayerContext'
import { MediaPlayerPlaceholder } from '@/components/media/MediaPlayerPlaceholder'
import { CopyButton } from '@/components/buttons/CopyButton'
import { JumpToPill } from '@/components/media/components/JumpToPill'
import { formatTime } from '@/views/Calls/components/helpers'
import { TranscriptProvider } from '@/components/transcript/TranscriptContext'
import {
  fetchMoreEvidenceForTask,
  fetchMediaForEvidenceCall,
  deleteEvidenceFromTask,
  deleteEvidenceInQueueAndReset,
} from '@/reducers/tasks/tasks.actions'
import { Countdown } from '@/components/interactions/Countdown'
import {
  addToCallIdDeleteQueue,
  removeFromCallIdDeleteQueue,
  setCallCountIncreased,
} from '@/reducers/tasks/tasks.redux'
import { isRemoveEvidenceDisabled } from '@/reducers/tasks/tasks.helpers'
import { Pill } from '@/components/pills/Pill'
import { COACHING_TASK_TYPE, COMPLIANCE_TASK_TYPE } from '@/reducers/tasks/tasks.constants'
import { ManualTaskModal } from '@/views/Calls/components/ManualTaskModal'
import { AddToPlaylist } from '@/views/Playlists/components/AddToPlaylist'
import { fetchPlaylists } from '@/reducers/playlists/playlists.actions'
import { PoweredByAI } from '@/components/ai/PoweredByAI'

export const CallEvidence = ({
  taskId,
  organizationId,
  agentId,
  taskIsOpen,
  fetchTasksWithFilters,
}) => {
  const dispatch = useDispatch()
  const { mediaPlayer } = useSelector((state) => state.media)
  const { task, callIdDeleteQueue, callIdDeleteCompleted } = useSelector((state) => state.tasks)
  const [evidenceWithMedia, setEvidenceWithMedia] = useState([])
  const [loadingCallIds, setLoadingCallIds] = useState([])
  const showCallEvidenceFeedbackType =
    task.type === COACHING_TASK_TYPE && task.ai_generated && !task.scorecard_criteria_scid
  const showAddToComplianceButton = task.type === COACHING_TASK_TYPE
  const showAddToCoachingButton = task.type === COMPLIANCE_TASK_TYPE

  const removeEvidenceDisabled = isRemoveEvidenceDisabled(
    task.evidence,
    callIdDeleteQueue,
    callIdDeleteCompleted
  )

  const handleAddToCallIdDeleteQueue = (callId) => {
    dispatch(addToCallIdDeleteQueue(callId))
  }

  const handleUndo = (callId) => {
    dispatch(removeFromCallIdDeleteQueue(callId))
  }

  const handleSelectMediaPlayer = (audioData = {}, videoData = {}, progress = 0) => {
    dispatch(
      updateMediaPlayer({
        callId: audioData.call_id,
        audioUrl: audioData.audio_url,
        audioUrlExpiration: audioData.audio_url_expiration,
        audioStatus: audioData.audio_status,
        videoUrls: videoData.video_urls,
        videoStatus: videoData.video_status,
        isPlaying: true,
        audioError: false,
        videoError: false,
        progress,
      })
    )
  }

  const handleDeleteCall = (callId) => {
    dispatch(deleteEvidenceFromTask(taskId, callId))
  }

  const handleQueueForRemoval = (callId) => {
    dispatch(resetMediaPlayer())
    handleAddToCallIdDeleteQueue(callId)
  }

  const handleJumpToTimestamp = (data = {}, timestamp = 0) => {
    // If working with the active media player, send a hard selected event to update it
    if (data.call_id === mediaPlayer.callId) {
      dispatch(updateMediaPlayer({ hardSelectedEvent: { timestamp } }))
    } else {
      // Otherwise, start new media player at the specified timestamp
      handleSelectMediaPlayer(data.call_audio, data.call_video, timestamp)
    }
  }

  const handleAddMoreEvidence = (evidenceType) => {
    dispatch(setCallCountIncreased())
    dispatch(fetchMoreEvidenceForTask(taskId, evidenceType))
  }

  // Load one call (detail + audio + screen capture) on demand, when the user
  // clicks "Load call" on that card. Deduped so a card already resolved or in
  // flight isn't fetched again.
  const handleLoadMedia = async (callId) => {
    const evidence = (task.evidence || []).find((item) => item.call_id === callId)
    if (!evidence || loadingCallIds.includes(callId)) {
      return
    }
    setLoadingCallIds((currentIds) => [...currentIds, callId])
    try {
      const evidenceWithLoadedMedia = await fetchMediaForEvidenceCall(evidence, dispatch)
      // Loading a call here is the coaching equivalent of opening the call page, which is what
      // the Supervisor Activity report counts as a view (CUS-485). Recorded only after the call
      // loaded, so a failed load that brings the button back cannot count again on retry.
      if (task.type === COACHING_TASK_TYPE) {
        dispatch(trackPageView(COACHING_CALL_DETAIL_PAGE, callId))
      }
      setEvidenceWithMedia((currentEvidence) =>
        currentEvidence.map((item) =>
          item.call_id === callId ? { ...item, ...evidenceWithLoadedMedia } : item
        )
      )
    } finally {
      setLoadingCallIds((currentIds) => currentIds.filter((id) => id !== callId))
    }
  }

  // Mirror task.evidence into local state. Media (call detail + audio + screen
  // capture) is NOT fetched here — it loads lazily per card via handleLoadMedia,
  // rather than eagerly for every evidence call when the task opens. The eager
  // version fired GET /api/calls/{id} + /audio + /screen_capture per related
  // call, so a task with many calls burst into hundreds of concurrent requests
  // and could trip per-endpoint rate limits (the desktop coaching flow already
  // loads on demand — RTG3-790). Media already loaded for a call is preserved
  // across evidence refreshes (e.g. "Add More Evidence") so it isn't reset.
  useEffect(() => {
    dispatch(resetMediaPlayer())
    setEvidenceWithMedia((currentEvidence) =>
      (task.evidence || []).map((evidence) => {
        const loaded = currentEvidence.find((item) => item.call_id === evidence.call_id)
        return loaded?.call_data
          ? {
              ...evidence,
              call_data: loaded.call_data,
              call_audio: loaded.call_audio,
              call_video: loaded.call_video,
            }
          : evidence
      })
    )
  }, [task.available_evidence_count])

  useEffect(() => {
    dispatch(fetchPlaylists(organizationId, false))

    return () => {
      console.info('[DELETE QUEUE]: Resolving queues on unmount')
      dispatch(deleteEvidenceInQueueAndReset(taskId, fetchTasksWithFilters))
    }
  }, [taskId])

  return (
    <>
      {!isEmpty(evidenceWithMedia) &&
        evidenceWithMedia.map((data) => {
          const isPositive = data.type === 'positive'
          const mediaIsAvailable =
            data.call_audio?.audio_status === 'available' ||
            data.call_video?.video_status === 'available'
          const isMediaLoaded = Boolean(data.call_data)
          const isMediaLoading = loadingCallIds.includes(data.call_id)
          const isMessageEvidence = data.call_type !== 'call'
          const evidenceId = isMessageEvidence ? data.message_id : data.call_id
          const evidenceLink = isMessageEvidence
            ? `/messages/${evidenceId}`
            : `/calls/${evidenceId}`
          return (
            <Card
              compact
              data-testid={`evidence-card-${data.call_id}`}
              className={classNames('call-evidence', {
                'delete-queue': callIdDeleteQueue.includes(data.call_id),
                'delete-completed': callIdDeleteCompleted.includes(data.call_id),
              })}
              key={data.call_id}
            >
              <div className="flex-align-center flex-space-between medium-gap mb">
                <div className="flex-align-center small-gap full-width">
                  <div className="flex-align-center small-gap">
                    <Link to={evidenceLink} target="_blank" className="balto-button text">
                      <strong>{evidenceId}</strong>
                    </Link>
                    <CopyButton
                      content={isMessageEvidence ? data.message_id : data.call_id}
                      showTooltip
                      iconOnly
                    />
                  </div>
                </div>
                {taskIsOpen && !callIdDeleteCompleted.includes(data.call_id) && (
                  <div className="flex-align-center small-gap">
                    {showCallEvidenceFeedbackType && (
                      <Pill
                        data-testid={`feedback-type-pill-${data.call_id}`}
                        critical={!isPositive}
                        success={isPositive}
                        small
                      >
                        {isPositive ? 'Positive' : 'Constructive'}
                      </Pill>
                    )}
                    {showAddToCoachingButton && (
                      <ManualTaskModal
                        callId={toString(data.call_id)}
                        callType={data.call_type}
                        userId={agentId}
                        organizationId={organizationId}
                        messageId={toString(data.message_id)}
                        type="coaching"
                      />
                    )}
                    {showAddToComplianceButton && (
                      <ManualTaskModal
                        callId={toString(data.call_id)}
                        userId={agentId}
                        callType={data.call_type}
                        organizationId={organizationId}
                        messageId={toString(data.message_id)}
                        type="compliance"
                      />
                    )}
                    <AddToPlaylist
                      callIds={[data.call_id]}
                      organizationId={organizationId}
                      shouldFetch={false}
                      callType={data.call_type}
                    />
                    {callIdDeleteQueue.includes(data.call_id) ? (
                      <Button
                        small
                        square
                        data-testid="undo-delete-evidence-button"
                        className="call-delete-undo-button"
                        onClick={() => handleUndo(data.call_id)}
                      >
                        <IconArrowBackUp className="icon-svg" />
                        <Countdown
                          time={5}
                          onExpire={() => {
                            handleDeleteCall(data.call_id)
                          }}
                        />
                      </Button>
                    ) : !removeEvidenceDisabled ? (
                      <Button
                        small
                        square
                        critical
                        data-testid="delete-evidence-button"
                        className="call-delete-button"
                        onClick={() => handleQueueForRemoval(data.call_id)}
                      >
                        <IconTrash />
                      </Button>
                    ) : null}
                  </div>
                )}
              </div>
              {data.ai_explanation_summary && (
                <h3 style={{ marginTop: 0 }}>{data.ai_explanation_summary}</h3>
              )}
              {data.ai_explanation && (
                <>
                  <p className="explanation">{data.ai_explanation}</p>
                  <PoweredByAI />
                </>
              )}
              {!isMessageEvidence &&
                (isMediaLoading ? (
                  <div className="empty mb">
                    <Loader label="Loading call..." />
                  </div>
                ) : !isMediaLoaded ? (
                  <Button
                    brand
                    data-testid={`load-call-${data.call_id}`}
                    onClick={() => handleLoadMedia(data.call_id)}
                  >
                    Load call
                  </Button>
                ) : !mediaIsAvailable ? (
                  <p className="audio-unavailable-text">Recording not available</p>
                ) : (
                  <>
                    {(data.manual_timestamp || data.ai_anchor_time) && (
                      <div className="mb">
                        {data.manual_timestamp && (
                          <JumpToPill
                            onClick={() => handleJumpToTimestamp(data, data.manual_timestamp)}
                            formattedTime={formatTime(data.manual_timestamp)}
                            dataTestId={data.call_id}
                          />
                        )}
                        {data.ai_anchor_time && (
                          <JumpToPill
                            onClick={() => handleJumpToTimestamp(data, data.ai_anchor_time)}
                            formattedTime={formatTime(data.ai_anchor_time)}
                            dataTestId={data.call_id}
                          />
                        )}
                      </div>
                    )}
                    <div>
                      {data.call_id === mediaPlayer.callId ? (
                        <Fragment key={data.call_id}>
                          <VideoPlayerProvider>
                            <AudioPlayerProvider>
                              <TranscriptProvider
                                transcript={data.call_data.transcript}
                                postcallTranscript={data.call_data.postcall_transcript}
                              >
                                <MediaPlayer
                                  hideShareButton
                                  showTranscriptButton
                                  isTranscriptAvailable
                                  transcriptMetadata={data.call_data.metadata}
                                  transcriptEnableAutoScroll
                                />
                              </TranscriptProvider>
                            </AudioPlayerProvider>
                          </VideoPlayerProvider>
                        </Fragment>
                      ) : (
                        <Fragment key={data.call_id}>
                          <MediaPlayerPlaceholder
                            callAudio={data.call_audio}
                            callVideo={data.call_video}
                            handleSelectMediaPlayer={(progress = 0) => {
                              handleSelectMediaPlayer(data.call_audio, data.call_video, progress)
                            }}
                          />
                        </Fragment>
                      )}
                    </div>
                  </>
                ))}
            </Card>
          )
        })}

      {(task?.hidden_constructive_evidence_count ?? 0) > 0 && (
        <div className="flex-center flex-align-center mt mb">
          <Button
            brand
            data-testid="add-constructive-evidence-btn"
            onClick={() => handleAddMoreEvidence('constructive')}
            disabled={!isEmpty(callIdDeleteQueue)}
            disabledTooltip="Deleting calls, please wait..."
          >
            Add More Constructive Evidence
          </Button>
        </div>
      )}
      {(task?.hidden_positive_evidence_count ?? 0) > 0 && (
        <div className="flex-center flex-align-center mt mb">
          <Button
            brand
            data-testid="add-positive-evidence-btn"
            onClick={() => handleAddMoreEvidence('positive')}
            disabled={!isEmpty(callIdDeleteQueue)}
            disabledTooltip="Deleting calls, please wait..."
          >
            Add More Positive Evidence
          </Button>
        </div>
      )}
    </>
  )
}
